import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  appViewSchema,
  assignableRoleSchema,
  viewsForRole,
  type CreateUserRequest,
} from "@/contracts";
import { z } from "zod";

import { createUserMutation, organizationOptionsQuery } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useLocale } from "@/i18n/use-locale";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";
import { ROLE_LABELS } from "./user-labels";
import { ViewAccessFields } from "./view-access-fields";

const inviteFormFields = z.object({
  organizationId: z.string(),
  email: z.email("Enter a valid email address.").trim().toLowerCase().max(254),
  name: z.string().trim().min(2, "Enter a name of at least 2 characters.").max(120),
  role: assignableRoleSchema,
  views: z.array(appViewSchema),
});

type InviteFormValues = z.infer<typeof inviteFormFields>;

export function UserInviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  const { locale } = useLocale();
  const queryClient = useQueryClient();

  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const organizations = useQuery({ ...organizationOptionsQuery(client), enabled: isSuperAdmin });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormFields),
    defaultValues: {
      organizationId: "",
      email: "",
      name: "",
      role: "USER",
      views: viewsForRole("USER"),
    },
  });

  const organizationId = useWatch({ control, name: "organizationId" });
  const role = useWatch({ control, name: "role" });
  const views = useWatch({ control, name: "views" });

  const mutation = useMutation({
    ...createUserMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      // The member count shown against each organization moves with this.
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  function close() {
    reset({
      organizationId: "",
      email: "",
      name: "",
      role: "USER",
      views: viewsForRole("USER"),
    });
    mutation.reset();
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    if (isSuperAdmin && !values.organizationId) {
      setError("organizationId", { message: "Choose the organization to invite them into." });

      return;
    }

    const body: CreateUserRequest = {
      email: values.email,
      name: values.name,
      role: values.role,
      views: values.views,
      locale,
      ...(values.organizationId ? { organizationId: values.organizationId } : {}),
    };

    try {
      await mutation.mutateAsync(body);
      close();
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fieldErrors())) {
          if (field in inviteFormFields.shape) {
            setError(field as keyof InviteFormValues, { message });
          }
        }
      }
    }
  });

  return (
    <FormDialog
      open={open}
      title={t("dialogInviteTitle")}
      description={t("dialogInviteDescription")}
      submitLabel={t("sendInvitation")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={close}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        {isSuperAdmin ? (
          <SelectField
            label={tCommon("organization")}
            required
            {...(errors.organizationId?.message === undefined
              ? {}
              : { error: errors.organizationId.message })}
            value={organizationId}
            onChange={(value) =>
              setValue("organizationId", value, { shouldValidate: true, shouldDirty: true })
            }
            data={[
              { value: "", label: t("chooseOrganization") },
              ...(organizations.data?.data ?? []).map((organization) => ({
                value: organization.id,
                label: organization.name,
              })),
            ]}
          />
        ) : null}

        <TextField
          label={tCommon("email")}
          type="email"
          required
          autoComplete="off"
          {...(errors.email?.message === undefined ? {} : { error: errors.email.message })}
          {...register("email")}
        />

        <TextField
          label={t("fieldFullName")}
          required
          {...(errors.name?.message === undefined ? {} : { error: errors.name.message })}
          {...register("name")}
        />

        {/* Super administrator is absent by design: it lives on the user
            record rather than on a membership, so no organization admin can
            grant it from here (ADR-016). */}
        <SelectField
          label={tCommon("role")}
          required
          hint={t("roleAdminHint")}
          value={role}
          onChange={(value) => {
            const nextRole = value as InviteFormValues["role"];
            setValue("role", nextRole, { shouldValidate: true, shouldDirty: true });
            setValue("views", viewsForRole(nextRole), { shouldDirty: true });
          }}
          data={assignableRoleSchema.options.map((roleOption) => ({
            value: roleOption,
            label: ROLE_LABELS[roleOption],
          }))}
        />

        <ViewAccessFields
          role={role}
          value={views ?? []}
          onChange={(next) => setValue("views", next, { shouldDirty: true })}
        />

        <Alert tone="info" title={t("dialogInviteDeliveryTitle")} announce={false}>
          {t("dialogInviteDeliveryBody")}
        </Alert>
      </div>
    </FormDialog>
  );
}
